import type { RemovedContactPoint } from './account-email'
import type { AuthAccountStatus } from './preload'

// Shapes Platform Core returns. Source IDs are always text, kept exactly as
// the source wrote them (POS integers, Booking CUIDs, Ticketing UUIDs), and
// never used as Hub's own keys (spec §4).

export type ContactPoint = {
  id: string
  kind: 'email' | 'phone' | 'address'
  value: string
  // Where the value came from, e.g. "bre-auth id_token" or "booking rental".
  provenance: string
  verifiedAt: string | null
  // Who said it is verified, e.g. "BRE Auth". Null when not verified.
  verifiedBy?: string | null
  createdAt: string
}

export type IdentityLink = {
  issuer: string
  subject: string
  linkedAt: string
  revokedAt: string | null
  provenance: string
  // What BRE Auth said about this account when Hub last asked (only when
  // drink tickets were pre-loaded by email or phone), and when; updated by a
  // sign-in or a held pre-load's check. Null when Hub never asked.
  authAccountStatus: AuthAccountStatus | null
  authAccountStatusAt: string | null
  // email_verified from BRE Auth's id token at the last sign-in to Hub, and
  // when that was. Null until they sign in. Display only.
  authEmailVerified: boolean | null
  authEmailVerifiedAt: string | null
  // Their first sign-in to Hub; null for a link Hub made by pre-loading
  // until they sign in.
  firstSignedInAt: string | null
  // From the people sync (ADR 0009): whether the account is an unclaimed
  // placeholder, when Auth deleted it, and when Hub last applied a change
  // from Auth. Null when the sync hasn't seen this account yet.
  authPlaceholder?: boolean | null
  authDeletedAt?: string | null
  authSyncedAt?: string | null
}

export type ExternalReference = {
  id: string
  environment: 'production' | 'development'
  sourceService: string
  entityType: string
  sourceId: string
  linkMethod: 'exact_subject' | 'manual' | 'import'
  createdAt: string
  // The source's own name for the record when it was linked: evidence for
  // whoever reads the link later, never used to match anything.
  sourceName: string | null
  // The staff member who linked it by hand; null for links Hub made itself
  // (an exact sign-in subject) or imported.
  linkedBy: { personId: string; displayName: string } | null
}

// A link with its whole history. Unlinking retires the row; nothing is
// deleted (spec §4, rule 6), and only active links are ever read as links.
export type ExternalReferenceHistory = ExternalReference & {
  retiredAt: string | null
  retiredBy: { personId: string; displayName: string } | null
  retiredReason: string | null
}

// account      a claimed BRE account
// placeholder  an unclaimed, staff-made BRE account
// deleted      the BRE account was deleted (or merged away)
export type BreAccountState = 'account' | 'placeholder' | 'deleted'

export type PersonSummary = {
  id: string
  displayName: string
  status: 'active' | 'inactive'
  primaryEmail: string | null
  // Their BRE account as Hub last knew it; null when they have none. Only
  // filled by the People list.
  breAccount?: BreAccountState | null
}

// How a person came to point at another: BRE Auth merged their accounts
// (ADR 0009), Booking proved an imported contact's account (ADR 0041), or
// staff merged a person with no BRE account (ADR 0055, mergeId set).
export type PersonMergeLink = { personId: string; displayName: string; at: string; caseId: string | null; by?: 'auth' | 'booking' | 'staff'; mergeId?: string | null; reason?: string | null }

export type Person = PersonSummary & {
  createdAt: string
  updatedAt: string
  identityLinks: IdentityLink[]
  contactPoints: ContactPoint[]
  // Contact points taken off the person, newest first (ADR 0057).
  removedContactPoints?: RemovedContactPoint[]
  externalReferences: ExternalReference[]
  memberships: MembershipSummary[]
  // Merged into another person: by BRE Auth (ADR 0009), a Booking proof
  // (ADR 0041) or staff (ADR 0055).
  mergedInto?: PersonMergeLink | null
  // People merged into this one, however.
  mergedFrom?: PersonMergeLink[]
}

export type OrganizationSummary = {
  id: string
  name: string
  kind: string | null
  status: 'active' | 'inactive'
}

export type MembershipSummary = {
  id: string
  personId: string
  personName: string
  organizationId: string
  organizationName: string
  relationship: string
  title: string | null
  effectiveFrom: string | null
  effectiveTo: string | null
}

export type AccessGrant = {
  id: string
  personId: string
  personName: string
  capability: string
  grantedByPersonId: string | null
  grantedAt: string
  validFrom: string
  validTo: string | null
  revokedAt: string | null
  note: string | null
  // Granted by position (ADR 0071): the rule and the record that holds
  // the position. Never revoked by hand; null for a grant given by hand.
  byPosition: { ruleId: string; sourceKind: string; sourceId: string; label: string } | null
}

// How Hub looks for one person (ADR 0012). "match" follows the device's
// own light or dark setting. Nobody who hasn't chosen gets anything but
// light.
export const THEME_PREFERENCES = ['light', 'dark', 'match'] as const
export type ThemePreference = (typeof THEME_PREFERENCES)[number]
export const DEFAULT_THEME: ThemePreference = 'light'
export const isThemePreference = (value: unknown): value is ThemePreference => (THEME_PREFERENCES as readonly unknown[]).includes(value)

// What core tells the web Worker about the person behind a sign-in.
export type Principal = {
  personId: string
  displayName: string
  capabilities: string[]
  theme: ThemePreference
}
